import { EmbeddablePublishedDatasourceDataQualityWarning } from '@tableau/data-catalog-client-components';
import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { ISanitizer } from '~commonInfra/utils/Sanitize';
import { SiteUrlsProps } from '~selectors/SiteUrlsSelectors';
import { dataQualityIndicatorInfoToDataQualityIndicator } from '~src/catalog/models/IDataQualityIndicatorInfo';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { ItemDescription } from '~src/spacesPlatform/components/header/HeaderItemDescription';
import { CertificationMetadata, OwnerMetadata } from '~src/spacesPlatform/components/header/HeaderMetadataRow';
import { ActionsMenuIcon, DetailButtonIcon, FavoriteToggleWithCountsIcon } from '~src/spacesPlatform/components/header/HeaderNameRowIcons';
import { ConnectedSpaceHeader } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { createContentButton } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';

import { ConnectedExtractMetadata } from '../../app/spaces/Header/ExtractMetadata';
import { ConnectedExtractTasksMetadata } from '../../app/spaces/Header/ExtractTasksMetadata';
import { CatalogApiRequests } from '../../catalog/CatalogApiRequests';
import { catalogApolloClient } from '../../catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '../../catalog/CatalogFeatureFlagsAdapter';
import { CERTIFIED_INDICATOR_TYPE, dataQualityIndicatorInfosToProps } from '../../catalog/components/dataQuality/DataQualityType';
import { DataCatalogUIActionsService } from '../../catalog/services/DataCatalogUIActionsService';
import { Messages } from '../../codegen/Localize';
import { FlowLinksWithIcon } from '../../flow/components/FlowLinksWithIcon';
import { FlowListItemInfo } from '../../flow/models/FlowInfo';
import { ISpaceItemDetailInfo } from '../../ts/models/ISpaceItemDetailInfo';
import { NewWorkbookEntityAction } from '../../workbook/entityActions/NewWorkbookEntityAction';
import { IDatasourceActions } from '../ServerApi/types/IDatasourceActions';
import { createEditDataSourceButton } from './DatasourceSpaceHeaderButtons';


const DATASOURCE_CREATE_CONTENT_FLYOUT_WIDTH: number = 260;

export interface DatasourceSpaceHeaderProps extends SiteUrlsProps {
  readonly item: ISpaceItemDetailInfo<'datasource'>;
  readonly availableActions: IDatasourceActions;
  readonly flows: ReadonlyArray<FlowListItemInfo>;
  readonly openDetail: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly editDatasource: () => void;
  readonly sanitizer: ISanitizer;
  readonly catalogingOnSiteEnabled: boolean;
  readonly editingFlowsEnabled: boolean;
}

export function DatasourceSpaceHeader(props: DatasourceSpaceHeaderProps): JSX.Element {
  const spotlightActions = props.editingFlowsEnabled ? undefined : [NewWorkbookEntityAction];
  return <>
    <ConnectedSpaceHeader
      item={props.item}
      availableActions={props.availableActions}
      nameRowIcons={
        <React.Fragment>
          {FavoriteToggleWithCountsIcon({
            item: props.item,
            addToFavorites: props.addToFavorites,
            removeFromFavorites: props.removeFromFavorites,
          })}
          {DetailButtonIcon(props.item, props.openDetail)}
          {ActionsMenuIcon(props.item)}
        </React.Fragment>
      }
      metadataRow={datasourceMetadataRow(props.item, props.flows, props.urls, props.availableActions, props.catalogingOnSiteEnabled)}
      descriptionElement={ItemDescription(props.item, props.openDetail, props.sanitizer)}
      spotlightActions={spotlightActions}
    />
    {props.editingFlowsEnabled ? <div className={`${ExplorerStyles.ToolbarButtonGroup} ${SpaceHeaderStyles.SpotlightActionButtonsRow}`} style={{ fontWeight: 'normal', }}>
      {createContentButton({
        // TFSID 1231210 will handle proper handling of create button, this is just a place holder for getting the details page to load
        projectId: props.item.project ? props.item.project.id : '',
        spaceType: props.item.contentType,
        flyoutWidth: DATASOURCE_CREATE_CONTENT_FLYOUT_WIDTH
      })}
      {createEditDataSourceButton({
        item: props.item,
        actions: props.availableActions,
        editDatasource: props.editDatasource,
        hasAssociatedFlows: props.flows.length > 0,
      })}
    </div> : null}
  </>;
}

export function datasourceMetadataRow(
  datasourceInfo: ISpaceItemDetailInfo<'datasource'>,
  flows: ReadonlyArray<FlowListItemInfo>,
  urls: SpacesSiteUrls,
  availableActions: IDatasourceActions,
  catalogingOnSiteEnabled: boolean,
): JSX.Element {
  return <React.Fragment>
    {OwnerMetadata(datasourceInfo.owner, urls)}
    <ConnectedExtractMetadata itemInfo={datasourceInfo} availableActions={availableActions} />
    <ConnectedExtractTasksMetadata itemInfo={datasourceInfo} availableActions={availableActions} />
    {PublishedByFlowMetadata(flows, urls)}
    {CertificationMetadata(datasourceInfo, urls)}
    {catalogingOnSiteEnabled &&
      <DataQualityMetadata
        datasourceInfo={datasourceInfo}
        urls={urls}
      />
    }
  </React.Fragment>;
}

function PublishedByFlowMetadata(flows: ReadonlyArray<FlowListItemInfo>, urls: SiteUrls): JSX.Element | null {
  if (flows.length > 0) {
    return (
      <>
        <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
          {Messages.datasourceHeaderPublishedByFlow()}
        </span>
        <span className={SpaceHeaderStyles.MetadataInfo}>
          <FlowLinksWithIcon flows={flows} hideIcon={true} siteUrls={urls} />
        </span>
      </>
    );
  } else {
    return null;
  }
}

function DataQualityMetadata(props: {
  datasourceInfo: ISpaceItemDetailInfo<'datasource'>;
  urls: SiteUrls;
}): JSX.Element {

  const anchoredWarnings = props.datasourceInfo.dataQualityIndicators.filter(t => t.type !== CERTIFIED_INDICATOR_TYPE);
  const dataQualityWarningsOnThisItem = dataQualityIndicatorInfosToProps(
    {
      assetUrl: props.urls.datasourceUrl(props.datasourceInfo.id),
      assetName: props.datasourceInfo.name,
      assetType: 'PublishedDatasource',
      onContentItem: true
    },
    anchoredWarnings
  );

  return (
    <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
      <EmbeddablePublishedDatasourceDataQualityWarning
        client={catalogApolloClient}
        vizportalUrlId={props.datasourceInfo.id}
        ownContentWarnings={dataQualityWarningsOnThisItem}
        siteUrls={props.urls}
        apiRequests={CatalogApiRequests}
        uiActions={DataCatalogUIActionsService.instance()}
        featureFlags={catalogFeatureFlagsAdapter()}
        asset={{
          assetName: props.datasourceInfo.name,
          assetType: 'PublishedDatasource',
          assetUrl: urls => urls.datasourceUrl(props.datasourceInfo.id)
        }}
        anchoredWarnings={anchoredWarnings.map(i => dataQualityIndicatorInfoToDataQualityIndicator(i))}
      />
    </span>
  );
}


